import { ContentioApi, UploadResponse } from '@jnext/ts-axios-corecontentio';
import { ColProps } from 'antd';
import axios, { AxiosRequestConfig } from 'axios';
import React, { FC, PropsWithChildren, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CONTENTIO_MIDDLEPATH, DEFAULT_CONTENTIO_MODULENAME, DEFAULT_CONTEXT_VALUES } from '../constants';

import { ContentIOConfig } from '../models';



export interface CompositorContextModel {
    /**
     * function for translate strings
     * @default TFunction
     * @memberof CompositorContextModel
     */
    translate: (code: string, params?: Record<string, any>) => string,
    /**
     * function for upload file
     * @default Error
     * @memberof CompositorContextModel
     */
    uploadFiles: (files: File[]) => Promise<UploadResponse[]>,
    /**
     * function for get url from file
     * @default Error
     * @memberof CompositorContextModel
     */
    getURLFromFileRef: (fileRef: string) => Promise<string>,
    /**
     * define shape of avatar crop and preview
     * @default 'circle'
     * @memberof CompositorContextModel
     */
    avatarShape: 'circle' | 'square',
    /**
     * Date format
     * @default 'DD/MM/YYYY'
     * @memberof CompositorContextModel
     */
    formatDate: string
    /**
     * Datetime format
     * @default 'DD/MM/YYYY HH:mm'
     * @memberof CompositorContextModel
     */
    formatDateTime: string,
    /**
     * Template Code
     * @default 'T1'
     * @type {string}
     * @memberof CompositorContextModel
     */
    templateCode: string,
    /**
     * Defines if the form display fields labels
     * @default false
     * @type {boolean}
     * @memberof CompositorContextModel
     */
    showFieldsLabel: boolean,
    /**
     * Override confirms field cols props
     * @default undefined
     * @type {ColProps}
     * @memberof CompositorContextModel
     */
    confirmsColProps: ColProps

}
export const CompositorContext = createContext<CompositorContextModel>(DEFAULT_CONTEXT_VALUES)

export type PartialCompositorContextModel = Partial<CompositorContextModel>

export interface CompositorProviderProps extends Omit<PartialCompositorContextModel, 'getURLFromFileRef' | 'getURLFromFileRef'> {
    /**
     * Content IO Configuration for file uploads
     *
     * @type {ContentIOConfig}
     * @memberof CompositorProviderProps
     */
    contentIOConfig?: ContentIOConfig
}

export const CompositorProvider: FC<PropsWithChildren<CompositorProviderProps>> = (props) => {
    const contentIOConfig = props.contentIOConfig
    const { t: translate } = useTranslation()
    const uploadFiles = async (files: File[]): Promise<UploadResponse[]> => {
        if (contentIOConfig) {
            const contentioApi: ContentioApi = new ContentioApi(
                undefined,
                contentIOConfig.servicePath,
                axios as any
            );
            const promises = []
            for (const file of files) {
                const requestParams: [string, File, string] = [
                    contentIOConfig.moduleName || DEFAULT_CONTENTIO_MODULENAME,
                    file,
                    contentIOConfig.middlePath || DEFAULT_CONTENTIO_MIDDLEPATH
                ]

                // @ts-ignore
                promises.push(contentioApi.privateUpload(...requestParams))
            }
            const response = (await Promise.all(promises)).flat().map(d => d.data[0])

            return response
        }
        return Promise.reject(new Error('[FORM_COMPOSITOR]: ContentIO not configured'))
    }

    const download = (fileRef: string, requestConfig?: AxiosRequestConfig) => {
        if (contentIOConfig) {
            const instance = new axios.Axios(requestConfig || {})
            const contentioApi: ContentioApi = new ContentioApi(
                undefined,
                contentIOConfig.servicePath,
                instance as any
            );
            const requestParams: [string] = [fileRef]

            return contentioApi.privateDownload(...requestParams);

        }
        return Promise.reject(new Error('[FORM_COMPOSITOR]: ContentIO not configured'))
    };

    const getURLFromFileRef = async (fileRef: string) => {
        if (contentIOConfig) {
            const res = await download(fileRef, { responseType: 'blob', headers: contentIOConfig.httpHeaders })
            if (res.status === 500 || res.data?.error) {
                return Promise.reject('')
            }
            return URL.createObjectURL(res.data)
        }
        return Promise.reject(new Error('[FORM_COMPOSITOR]: ContentIO not configured'))
    }
    return (
        <CompositorContext.Provider value={{
            avatarShape: props.avatarShape ?? DEFAULT_CONTEXT_VALUES.avatarShape,
            formatDate: props.formatDate ?? DEFAULT_CONTEXT_VALUES.formatDate,
            formatDateTime: props.formatDateTime ?? DEFAULT_CONTEXT_VALUES.formatDateTime,
            translate: props.translate ?? translate,
            templateCode: props.templateCode ?? DEFAULT_CONTEXT_VALUES.templateCode,
            showFieldsLabel: props.showFieldsLabel ?? DEFAULT_CONTEXT_VALUES.showFieldsLabel,
            confirmsColProps: props.confirmsColProps ?? DEFAULT_CONTEXT_VALUES.confirmsColProps,
            getURLFromFileRef: getURLFromFileRef,
            uploadFiles: uploadFiles
        }}>
            {props.children}
        </CompositorContext.Provider>
    )
}

export const useCompositorContext = () => useContext(CompositorContext)